import React, { useEffect, useState } from 'react';
import Header from 'components/ui/Header';
import Sidebar from 'components/ui/Sidebar';
import Icon from 'components/AppIcon';
import { AreaChart, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Area } from 'recharts';
import RecentActivity from './components/RecentActivity';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';


const Dashboard = () => {
  const token = localStorage.getItem('token');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [userRole, setUserRole] = useState('staff');
  const [successScanData, setSuccessScanData] = useState([]); // Mock user role
  const [failedScanData, setFailedScanData] = useState([]);
  const [requestedClient, setRequestedClient] = useState('');
  const [activities, setActivities] = useState([])
  const [permissions, setPermissions] = useState([]);
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const [buttonLoading, setButtonLoading] = useState(false)
  const navigate = useNavigate()

  const scanlog = async () => {
    if (currentUser.role === 'Client') {
      const response = await axios.get(`${import.meta.env.VITE_APP_ADMINURL}/api/scanlogs/${currentUser.id}`)
      // console.log(response.data)
      setFailedScanData(response.data.failed)
      setSuccessScanData(response.data.success)
    }
    else {
      const response = await axios.get(`${import.meta.env.VITE_APP_ADMINURL}/api/allscanlogs`)
      // console.log(response.data)
      setFailedScanData(response.data.failed)
      setSuccessScanData(response.data.success)
    }
  }
  const combineScanData = (successScanData, failedScanData) => {
    const combineMap = {};
    successScanData.forEach(item => {
      const date = item.date;
      combineMap[date] = { date, success: item.count, failed: 0 };
    });

    failedScanData.forEach(item => {
      const date = item.date;
      if (combineMap[date]) {
        combineMap[date].failed = item.count;
      }
      else {
        combineMap[date] = { date, success: 0, failed: item.count }
      }
    });
    return Object.values(combineMap)
  }

  let combinedScanData = combineScanData(successScanData, failedScanData);
  combinedScanData.sort((a, b) => {
    const [dayA, monthA, yearA] = a.date.split('-').map(Number);
    const [dayB, monthB, yearB] = b.date.split('-').map(Number);

    const dateA = new Date(2000 + yearA, monthA - 1, dayA); // Adding 2000 to YY
    const dateB = new Date(2000 + yearB, monthB - 1, dayB);

    return dateA - dateB;
  })

  const activitylog = async () => {
    if (currentUser) {
      // console.log(currentUser.role.toLowerCase())
      if (currentUser.role.toLowerCase() === 'admin') {
        const response = await axios.get(`${import.meta.env.VITE_APP_ADMINURL}/api/login_history`)
        const loginActivity = response?.data.map((login) => {
          return { ...login, type: 'login', title: 'Logged In' }
        })
        // console.log(loginActivity)
        const scanResponse = await axios.get(`${import.meta.env.VITE_APP_ADMINURL}/api/scan_logs`)
        const scanlogsActivity = scanResponse.data.map((data) => {
          return { ...data, type: 'scan', title: 'Scan', date: data.createdAt }
        })
        // console.log(scanlogsActivity)
        setActivities([...loginActivity, ...scanlogsActivity])
      }
    }
  }

  const handleDownload = async () => {
    setButtonLoading(true)
    try {
      const url = `${import.meta.env.VITE_APP_ADMINURL}/api/exportQrdata?company=All`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        console.error("Export failed");
        return;
      }

      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "qrcode_export.xlsx";
      link.click();
      window.URL.revokeObjectURL(link.href);
      setButtonLoading(false)
    } catch (error) {
      console.log(error)
      setButtonLoading(false)
    }
  };

  useEffect(() => {
    scanlog()
    activitylog()
  }, [])

  useEffect(() => {
    const fetchPermission = async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_APP_ADMINURL}/api/permission`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status !== 200) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate("/admin", { replace: true }); // ✅ use replace
        return;
      }

      const availablePermissions = await response.json();
      setPermissions(availablePermissions);
    };

    if (currentUser) fetchPermission();
  }, []);


  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };




  return (
    <div className="min-h-screen bg-background">
      <Header
        user={currentUser}
        onMenuToggle={handleSidebarToggle}
        sidebarCollapsed={sidebarCollapsed}
      />

      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
        permissions={permissions}
      />

      <main className={`
        pt-header-height nav-transition
        ${sidebarCollapsed ? 'lg:ml-sidebar-collapsed' : 'lg:ml-sidebar-width'}
      `}>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl font-heading font-bold text-text-primary mb-2">
                  Dashboard
                </h1>
                <p className="text-text-secondary">
                  Welcome back, {currentUser.company_name}. Here's your Scan Report.
                </p>
              </div>
              <div className="mt-4 lg:mt-0 flex items-center space-x-3">
                <div className="flex items-center space-x-2 text-sm text-text-secondary">
                  <Icon name="Calendar" size={16} color="#7f8c8d" />
                  <span>Last updated: {new Date().toLocaleDateString()}</span>
                </div>
                <button className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-700 nav-transition flex items-center space-x-2" onClick={handleDownload} disabled={buttonLoading} >
                  <Icon name="Download" size={16} color="white" />
                  <span>Export Report</span>
                </button>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
            {/* Revenue Trends */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-heading font-semibold text-text-primary">Usage</h3>
                  <p className="text-sm text-text-secondary">Monthly Usage</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 rounded-lg hover:bg-background nav-transition">
                    <Icon name="MoreHorizontal" size={16} color="#7f8c8d" />
                  </button>
                </div>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={combinedScanData} margin={{ right: 10 }}>
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip
                      formatter={(value, name, props) => {
                        const total = props.payload.success + props.payload.failed;
                        return [value, name === 'success' ? 'Success Scan' : 'Failed Scan'];
                      }}
                      labelFormatter={(label, payload = []) => {
                        if (payload.length === 0 || !payload[0]?.payload) {
                          return label;
                        }
                        const total = payload[0].payload.success + payload[0].payload.failed;
                        return `Total Scan: ${total}`;
                      }} />
                    <Legend />
                    <Bar dataKey="success" stackId="a" fill="#27ae60" />
                    <Bar dataKey="failed" stackId="a" fill="#f39c12" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Failed Scan Log */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-heading font-semibold text-text-primary">Failed QR Scan</h3>
                  <p className="text-sm text-text-secondary">Current month failed QR Scan</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 rounded-lg hover:bg-background nav-transition">
                    <Icon name="MoreHorizontal" size={16} color="#7f8c8d" />
                  </button>
                </div>
              </div>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%" >
                  <AreaChart data={failedScanData} margin={{ right: 10 }}>
                    <defs>
                      <linearGradient id="count" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#dd2c2f" stopOpacity={1} />
                        <stop offset="95%" stopColor="#dd2c2f" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" stroke='#dd2c2f' dataKey="count" fill="url(#count)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* daily Scan Report */}
          <div className="bg-surface rounded-lg border border-border p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-heading font-semibold text-text-primary">Daily Scan Report</h3>
                <p className="text-sm text-text-secondary">QR Scan Report</p>
              </div>
              <div className="flex items-center space-x-2">
                <select className="text-sm border border-border rounded-lg px-3 py-2 bg-surface text-text-primary">
                  <option>Last 30 days</option>
                  <option>Last 90 days</option>
                  <option>Last 6 months</option>
                </select>
              </div>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={combinedScanData}>
                  <XAxis dataKey="date" stroke="#7f8c8d" fontSize={12} />
                  <YAxis stroke="#7f8c8d" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e1e8ed',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="success"
                    stroke="#27ae60"
                    fill="#27ae60"
                    fillOpacity={0.6}
                    name="success"
                  />
                  <Area
                    type="monotone"
                    dataKey="failed"
                    stroke="#f00"
                    fill="#f00"
                    fillOpacity={0.5}
                    name="failed"
                  />

                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="grid grid-cols-1 gap-6">
            {/* Recent Activity */}
            {currentUser.role.toLowerCase() === 'admin' && <div>
              <RecentActivity activities={activities} />
            </div>}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;